import type { ComponentProps, MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { NavItemType } from 'types/menu';
import { cn } from 'utils/cn';
import { FOCUS_RING_INSET } from '../../focusRing';

type Props = Omit<ComponentProps<'a'>, 'href' | 'target'> & { item: NavItemType };

/**
 * A menu entry's anchor: a router link, or a plain one for an `external` entry. A disabled entry stays
 * in place but inert. A click on the page already open does not navigate again, so the list keeps its
 * state, as in the classic menu. It draws the focus ring of a row, inside it; a caller that places it as a
 * free-standing control passes the outside ring in `className`.
 */
function MenuLink({ item, className, onClick, ...props }: Props) {
    const { pathname } = useLocation();

    const shared = {
        target: item.target,
        className: cn(FOCUS_RING_INSET, item.disabled && 'pointer-events-none opacity-38', className),
        ...(item.disabled ? { 'aria-disabled': true, tabIndex: -1 } : {}),
        ...props,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event);
            if (pathname === item.url?.split('?')[0]) {
                event.preventDefault();
            }
        },
    };

    return item.external ? <a href={item.url} {...shared} /> : <Link to={item.url ?? ''} {...shared} />;
}

export default MenuLink;
